import api from '@/lib/api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: 'customer' | 'agent' | 'admin';
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'customer' | 'agent' | 'admin' | 'warehouse';
  emailVerified: boolean;
  status?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
    tokenType: string;
  };
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    
    if (response.data.success) {
      const { accessToken, refreshToken, user } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }
    
    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    
    if (response.data.success) {
      const { accessToken, refreshToken, user } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }
    
    return response.data;
  }

  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async getProfile(): Promise<{ user: User; activeSessionCount: number }> {
    const response = await api.get('/auth/me');
    return response.data.data;
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      // Continue with local logout even if API call fails
      console.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await api.post<AuthResponse>('/auth/refresh', { refreshToken });
    
    if (response.data.success) {
      const { accessToken, refreshToken: newRefreshToken, user } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }
    
    return response.data;
  }

  async impersonate(userId: string): Promise<{ accessToken: string; user: User }> {
    // Admin auth'unu sessionStorage'a yedekle
    const currentAccessToken = localStorage.getItem('accessToken');
    const currentRefreshToken = localStorage.getItem('refreshToken');
    const currentUser = localStorage.getItem('user');

    if (currentAccessToken) sessionStorage.setItem('admin_accessToken', currentAccessToken);
    if (currentRefreshToken) sessionStorage.setItem('admin_refreshToken', currentRefreshToken);
    if (currentUser) sessionStorage.setItem('admin_user', currentUser);

    const response = await api.post(`/auth/impersonate/${userId}`);

    if (response.data.success) {
      const { accessToken, user } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      // Refresh token yok — impersonation token refresh edilemez
      localStorage.removeItem('refreshToken');
    }

    return response.data.data;
  }

  stopImpersonation(): void {
    const adminAccessToken = sessionStorage.getItem('admin_accessToken');
    const adminRefreshToken = sessionStorage.getItem('admin_refreshToken');
    const adminUser = sessionStorage.getItem('admin_user');

    if (adminAccessToken) localStorage.setItem('accessToken', adminAccessToken);
    if (adminRefreshToken) localStorage.setItem('refreshToken', adminRefreshToken);
    if (adminUser) localStorage.setItem('user', adminUser);

    sessionStorage.removeItem('admin_accessToken');
    sessionStorage.removeItem('admin_refreshToken');
    sessionStorage.removeItem('admin_user');
  }

  isImpersonating(): boolean {
    return !!sessionStorage.getItem('admin_accessToken');
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }
}

export default new AuthService();